/**
 * v2's toast (2026-10-05, the user: one toast for both apps, no platform split, the existing
 * design). The queue lives here so core can raise one; `@rista/ui/library/components/Toast`'s `ToastHost`
 * draws it, mounted once by each app's shell. Nothing runs at module load.
 *
 * Behaviour is web v1's (`commonComponents/ToastNotify`, react-toastify): one toast at a time, a
 * toast raised while one shows is dropped (v1 gives every toast one `toastId`), 3 s, closed by a
 * press. Mobile v1 replaced instead; v2 takes web's (rule 9, docs/revamps/toast.md).
 */
export type ToastKind = "info" | "success" | "error";

export interface ToastMessage {
  id: number;
  kind: ToastKind;
  message: string;
}

/** react-toastify's `autoClose` in v1 */
export const TOAST_MS = 3000;

export interface Toasts {
  /** false when dropped: a toast is already showing, or there is nothing to say */
  show(kind: ToastKind, message: string): boolean;
  dismiss(): void;
  current(): ToastMessage | null;
  subscribe(listener: () => void): () => void;
}

// ponytail: one host, at the app root; a toast above a native RN Modal needs a host inside that
// modal and a "top host draws" rule, added when a v2 dialog frame (L0) needs one.
export function createToasts(timers: { set: typeof setTimeout; clear: typeof clearTimeout } = { set: setTimeout, clear: clearTimeout }): Toasts {
  // 🔴 called UNBOUND: a browser's `setTimeout` called as `timers.set(...)` runs with `this` = the
  // object and throws "Illegal invocation" (node does not care, so no test saw it until P1 slice 5's
  // browser check). Holding the functions, not the object, is right for any timers handed in.
  const { set, clear } = timers;
  let current: ToastMessage | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let seq = 0;
  const listeners = new Set<() => void>();
  const emit = () => listeners.forEach((l) => l());
  const toasts: Toasts = {
    show(kind, message) {
      if (current || !message) return false;
      current = { id: ++seq, kind, message };
      timer = set(toasts.dismiss, TOAST_MS);
      emit();
      return true;
    },
    dismiss() {
      if (!current) return;
      clear(timer);
      current = null;
      emit();
    },
    current: () => current,
    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
  };
  return toasts;
}

/** the process's one queue */
export const toasts = createToasts();
